import { z } from "zod";

// Generated attribute type enum - maps to backend types
export const generatedAttributeTypes = [
  "unique_id",
  "name",
  "amount",
  "date",
  "generic_string",
  "generic_number",
  "sample_number",
] as const;

export const GeneratedAttributeTypeSchema = z.enum(generatedAttributeTypes);
export type GeneratedAttributeType = z.infer<
  typeof GeneratedAttributeTypeSchema
>;

// Table bounds for Excel files
export const TableBoundsSchema = z
  .object({
    startRow: z.number(),
    startCol: z.number(),
    endRow: z.number(),
    endCol: z.number(),
  })
  .nullable();

export type TableBounds = z.infer<typeof TableBoundsSchema>;

// Parsed column from the backend
export const ParsedColumnSchema = z.object({
  columnIndex: z.number(),
  columnName: z.string(),
  detectedType: GeneratedAttributeTypeSchema,
  sampleValues: z.array(z.union([z.string(), z.number(), z.null()])),
  confidence: z.number(),
});

export type ParsedColumn = z.infer<typeof ParsedColumnSchema>;

// Full parsed file preview response
export const ParsedFilePreviewSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  fileType: z.enum(["csv", "xlsx", "xls", "xlsm"]),
  tableBounds: TableBoundsSchema,
  columns: z.array(ParsedColumnSchema),
  previewRows: z.array(
    z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
  ),
  totalRowCount: z.number(),
  isFirstUpload: z.boolean(),
});

export type ParsedFilePreview = z.infer<typeof ParsedFilePreviewSchema>;

// Column selection for first upload (creating new attributes)
// Note: Request bodies use snake_case (backend expects snake_case)
export const ColumnSelectionSchema = z.object({
  column_index: z.number(),
  include: z.boolean(),
  attribute_type: GeneratedAttributeTypeSchema,
  attribute_name: z.string().optional(),
  source_row_display: z.boolean().optional(),
});

export type ColumnSelection = z.infer<typeof ColumnSelectionSchema>;

// Column mapping for subsequent uploads (mapping to existing attributes)
export const ColumnAttributeMappingSchema = z.object({
  column_index: z.number(),
  procedure_attribute_id: z.string().nullable(),
});

export type ColumnAttributeMapping = z.infer<
  typeof ColumnAttributeMappingSchema
>;

// Request body for confirming first upload
export const ConfirmFirstUploadRequestSchema = z.object({
  file_id: z.string(),
  column_selections: z.array(ColumnSelectionSchema),
});

export type ConfirmFirstUploadRequest = z.infer<
  typeof ConfirmFirstUploadRequestSchema
>;

// Request body for confirming subsequent upload
export const ConfirmSubsequentUploadRequestSchema = z.object({
  file_id: z.string(),
  column_mappings: z.array(ColumnAttributeMappingSchema),
});

export type ConfirmSubsequentUploadRequest = z.infer<
  typeof ConfirmSubsequentUploadRequestSchema
>;

// Response from confirm endpoint
export const SampleUploadResultSchema = z.object({
  createdSampleCount: z.number(),
  createdAttributeCount: z.number(),
  status: z.enum(["success", "partial", "error", "completed"]),
  message: z.string().optional(),
});

export type SampleUploadResult = z.infer<typeof SampleUploadResultSchema>;
